import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  FormGroupDirective,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AppContextService,
  EcaGenericMessageDialogComponent,
  EcaGenericMessageDialogConfig,
  WindowService,
  EcaPillarBoxModule,
  EcaSpinner,
} from '@drc-eca/eca-components-lib';
import { environment } from '../../../environments/environment';
import { UserManagementService } from '../../user/services/user-management.service';
import { LayoutDirective, LayoutAlignDirective, FlexDirective } from '@ngbracket/ngx-layout/flex';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { ProductContextService } from '../services/product-context.service';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    EcaPillarBoxModule,
    LayoutDirective,
    LayoutAlignDirective,
    FlexDirective,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatButton,
    EcaSpinner,
  ],
})
export class ForgotPasswordComponent implements OnInit {
  private appContextService = inject(AppContextService);
  private productContextService = inject(ProductContextService);
  private userManagementService = inject(UserManagementService);
  private windowService = inject(WindowService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);

  form: UntypedFormGroup;
  loginRoute: string = '/login';
  productCode!: string;
  isSaving = false;

  constructor() {
    const fb = inject(UntypedFormBuilder);

    const emailPattern = `^[a-zA-Z0-9!#$%&'*+/=?^_‘{|}~-]+(?:\\.[a-zA-Z0-9!#$%&'*+\\/=?^_‘{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$`;

    this.form = fb.group({
      userEmail: ['', Validators.pattern(emailPattern)],
    });
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      if (params['productCode']) {
        this.productCode = params['productCode'];
        this.loginRoute = `/login/${this.productCode}`;
        this.productContextService.setSelectedProductCode(this.productCode);
      }
    });

    this.appContextService.getSelectedProductCode().subscribe((productCode) => {
      this.productContextService.syncTenant(this.productCode || productCode);
      if (productCode && !this.productCode) {
        this.productCode = productCode;
        this.loginRoute = `/login/${productCode}`;
      }
    });
  }

  createLoginLink(e: Event) {
    e.preventDefault();
    if (
      this.windowService.location !== this.windowService['window'].parent.location &&
      environment.islandBrowserUrl
    ) {
      // Must be coming from the embedded login from Island browser.
      this.windowService.location.href = environment.islandBrowserUrl;
    } else {
      // Else we'll let the click continue to the router
      this.router.navigate([this.loginRoute]);
    }
  }

  onSubmit(frm: UntypedFormGroup, formDirective: FormGroupDirective) {
    const message =
      'If you entered a valid email address, we will send you an email with instructions on how to change your password.';
    if (frm.invalid) {
      return;
    }
    this.isSaving = true;
    this.userManagementService
      .forgotPassword(frm.controls.userEmail.value, this.productCode)
      .subscribe(
        () => {
          this.isSaving = false;
          frm.reset();
          formDirective.resetForm();
          this.openGoodModal(message);
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (err) => {
          this.isSaving = false;
          this.openGoodModal(message);
        },
      );
  }

  openGoodModal(inMessage: string) {
    const data: EcaGenericMessageDialogConfig = {
      title: 'Forgot Your Password?',
      message: `<p>${inMessage}</p>`,
    };
    this.dialog.open(EcaGenericMessageDialogComponent, { data });
  }
}
